// Game stats bar: moves, a count-up clock, wins of games played and personal bests, all kept on this device
// (owner decision 2026-10-08). The clock is shown by default and hidden with Settings → Timer; it never limits
// play (no countdowns, WCAG 2.2.1), pauses while the tab is hidden, and is saved with the game.
// Games call gameProgress() on every render; everything else happens here.
import { readJSON, writeJSON } from "./store"

export interface Progress {
  key: string // the game's save key: one clock per saved game
  ruleset: string // stats bucket, e.g. "klondike:draw-1"
  id: string // deal / puzzle identity; a new id means a new game
  moves?: number // omitted where a game has no moves (Sudoku, Word Search)
  /** The player has made a move. Default: moves above the count when this deal was first seen, so a deal's
   *  automatic opening moves (an ace sent to its foundation) don't start the clock. */
  started?: boolean
  won: boolean
}

/** Clock for one deal. `counted`: this deal is in "played"; `won`: its win is recorded (never undone, so Undo and
 *  Redo of the winning move can't count it twice); `done`: the board is won now, so the clock is stopped; `base`:
 *  the move count when the deal was first seen. */
export interface Clock { id: string; ms: number; counted: boolean; done: boolean; won?: boolean; base: number }
export interface Totals { played: number; won: number; bestMs?: number; bestMoves?: number }
export type StatsMap = Record<string, Totals>

export const STATS_KEY = "dt:stats:v1"
const clockKey = (key: string): string => `dt:clock:${key}`
/** Clocks kept per saved game, newest first, so Undo back past New game or Restart finds the old deal's clock
 *  (8 was too few: nine New games then nine Undos lost the first game's clock; bug hunt round 2). */
const KEEP = 64
type Book = Record<string, Clock & { at?: number }>

export function formatTime(ms: number): string {
  const s = Math.floor(ms / 1000)
  const h = Math.floor(s / 3600), m = Math.floor((s % 3600) / 60), r = s % 60
  return h ? `${h}:${String(m).padStart(2, "0")}:${String(r).padStart(2, "0")}` : `${m}:${String(r).padStart(2, "0")}`
}

export interface Step { clock: Clock; totals: Totals; running: boolean; win?: { ms: number; newBestTime: boolean; newBestMoves: boolean } }

/** A number for a fresh start of a deal (a new game, a new puzzle or a Restart), part of the deal id the games
 *  report. Unique, so a deal dealt again, or a second Restart after undoing the first, never picks up an earlier
 *  attempt's clock (bug hunt round 2: a counter could repeat once its attempt was undone). */
let lastAttempt = 0
export function newAttempt(): number {
  lastAttempt = Math.max(Date.now(), lastAttempt + 1)
  return lastAttempt
}

/** Pure: the next clock and totals after a progress report. `prev` is this deal's clock (null for a deal not seen
 *  before) and `ms` its elapsed time up to now. A Restart is a new deal id (games add an attempt number). */
export function step(prev: Clock | null, totals: Totals, p: Progress, ms: number): Step {
  const begun = (c: Clock): boolean => p.started ?? (p.moves ?? 0) > c.base
  let clock: Clock = !prev || prev.id !== p.id
    ? { id: p.id, ms: 0, counted: false, done: false, base: p.moves ?? 0 }
    : { ...prev, ms }
  const started = begun(clock)
  let t = totals
  if (started && !clock.counted) { clock = { ...clock, counted: true }; t = { ...t, played: t.played + 1 } }
  let win: Step["win"]
  if (p.won && !clock.done) {
    clock = { ...clock, done: true }
    if (!clock.won) {
      if (!clock.counted) { clock = { ...clock, counted: true }; t = { ...t, played: t.played + 1 } }
      const newBestTime = t.bestMs !== undefined && clock.ms < t.bestMs
      const newBestMoves = p.moves !== undefined && t.bestMoves !== undefined && p.moves < t.bestMoves
      t = {
        ...t, won: t.won + 1,
        bestMs: Math.min(t.bestMs ?? Infinity, clock.ms),
        ...(p.moves !== undefined ? { bestMoves: Math.min(t.bestMoves ?? Infinity, p.moves) } : {}),
      }
      clock = { ...clock, won: true }
      win = { ms: clock.ms, newBestTime, newBestMoves }
    }
  } else if (!p.won && clock.done) {
    clock = { ...clock, done: false } // undo after a win: timing resumes; the win stays counted, once
  }
  return { clock, totals: t, running: started && !p.won, win }
}

// ---- DOM ------------------------------------------------------------------------------------
let cur: Progress | null = null
let book: Book = {}
let clock: Clock | null = null
let since: number | null = null // performance.now() when the clock last resumed
let running = false
let tick: number | undefined
let wired = false

const elapsed = (): number => (clock?.ms ?? 0) + (since !== null ? performance.now() - since : 0)
const bar = (): HTMLElement | null => document.querySelector("[data-stats]")
const set = (name: string, text: string): void => { const el = bar()?.querySelector(`[data-stat="${name}"]`); if (el && el.textContent !== text) el.textContent = text }

/** The saved clocks for one game; an older save held a single clock, which becomes a one-entry book. */
function readBook(key: string): Book {
  const raw = readJSON<Book | Clock>(clockKey(key))
  if (!raw) return {}
  if (typeof (raw as Clock).id === "string" && typeof (raw as Clock).ms === "number") return { [(raw as Clock).id]: raw as Clock }
  return raw as Book
}

function persist(): void {
  if (!cur) return
  if (clock) book[clock.id] = { ...clock, ms: Math.round(elapsed()), at: Date.now() }
  const ids = Object.keys(book).sort((a, b) => (book[b]!.at ?? 0) - (book[a]!.at ?? 0))
  for (const id of ids.slice(KEEP)) delete book[id]
  writeJSON(clockKey(cur.key), book)
}

function setRunning(on: boolean): void {
  const go = on && document.visibilityState !== "hidden"
  if (go && since === null) since = performance.now()
  if (!go && since !== null) { clock = clock && { ...clock, ms: elapsed() }; since = null; persist() }
  if (go && tick === undefined) tick = window.setInterval(() => { show(); if (Math.round(elapsed() / 1000) % 5 === 0) persist() }, 1000)
  if (!go && tick !== undefined) { clearInterval(tick); tick = undefined }
}

function show(): void {
  if (!cur) return
  const t = (readJSON<StatsMap>(STATS_KEY) ?? {})[cur.ruleset] ?? { played: 0, won: 0 }
  if (cur.moves !== undefined) set("moves", String(cur.moves))
  set("time", formatTime(elapsed()))
  set("record", `${t.won} of ${t.played}`)
  const best = [t.bestMs !== undefined ? formatTime(t.bestMs) : "", t.bestMoves !== undefined ? `${t.bestMoves} moves` : ""].filter(Boolean).join(", ")
  set("best", best || "none yet")
}

/** Called by every game on each render. */
export function gameProgress(p: Progress): void {
  if (!wired) {
    wired = true
    document.addEventListener("visibilitychange", () => setRunning(running))
    window.addEventListener("pagehide", persist)
  }
  if (cur?.key !== p.key) { setRunning(false); book = readBook(p.key); clock = null; since = null }
  if (clock?.id !== p.id) {
    // A different deal (New game, Restart, or Undo back past one): park this clock and pick up that deal's.
    setRunning(false)
    if (clock) book[clock.id] = { ...clock, at: Date.now() }
    clock = book[p.id] ?? null
  }
  const all = readJSON<StatsMap>(STATS_KEY) ?? {}
  const before = all[p.ruleset] ?? { played: 0, won: 0 }
  const ms = elapsed()
  const s = step(clock, before, p, ms)
  if (since !== null) since = performance.now() // the elapsed time so far is now in clock.ms
  clock = s.clock
  cur = p
  if (s.totals !== before) writeJSON(STATS_KEY, { ...all, [p.ruleset]: s.totals })
  running = s.running
  setRunning(running)
  persist()
  show()
  if (s.win) announceWin(s.win)
  // lib/finish.ts shows the "what next" card over a finished board, including one restored as finished on reload.
  document.dispatchEvent(new CustomEvent("dt:progress", { detail: { won: p.won, fresh: !!s.win } }))
}

/** Adds the time to the game's own win message (after it is written). */
function announceWin(w: NonNullable<Step["win"]>): void {
  setTimeout(() => {
    const status = document.querySelector<HTMLElement>("[data-status]")
    if (!status) return
    const timer = document.documentElement.dataset.timer !== "off"
    const extra = [
      timer ? `Time ${formatTime(w.ms)}.` : "",
      timer && w.newBestTime ? "A new best time!" : w.newBestMoves ? "Your fewest moves yet!" : "",
    ].filter(Boolean).join(" ")
    if (extra) status.textContent = `${status.textContent ?? ""} ${extra}`
  }, 0)
}
